import ExperimentSummary, { type SummaryData } from '../ExperimentSummary';
import { FlowContainer, FlowNode, FlowArrow, FlowTerminal, FlowDecision, FlowNote } from '../FlowPrimitives';

// ============================================================================
// Experiment 11 - Potentiometric Determination of Aspirin in Commercial Tablets
// ============================================================================

const summaryData: SummaryData = {
  experimentId: 'exp11',
  title: 'Experiment 11: Aspirin Determination',
  objectives: [
    'Perform a potentiometric titration of aspirin with standardized NaOH',
    'Calibrate and properly use a pH meter with combination electrode',
    'Construct a titration curve (pH vs. volume of NaOH)',
    'Determine the endpoint using the first derivative method (ΔpH/ΔV)',
    'Calculate the aspirin content and purity of a commercial tablet',
  ],
  safetyNotes: [
    'NaOH is caustic — avoid skin contact, rinse immediately if spilled',
    'pH electrode is fragile and expensive — handle with care, never let it dry out',
    'Do not touch the glass bulb of the electrode with bare hands',
    'Wear safety goggles and gloves at all times',
  ],
  equipment: [
    'pH meter with combination electrode',
    '50 mL burette',
    'Burette clamp and ring stand',
    'Magnetic stirrer and stir bar',
    '250 mL beaker (titration vessel)',
    'Wash bottle with distilled water',
    'Analytical balance',
    'Hot plate (if warming needed to dissolve)',
  ],
  chemicals: [
    'Buffer solutions: pH 4.00, pH 7.00',
    '0.1 M NaOH solution',
    '0.10 M HCl standard solution',
    'Aspirin tablets (4)',
    '95% ethanol (CH₃CH₂OH)',
    'Distilled water',
  ],
  procedure: [
    {
      number: 1,
      title: 'Calibrate pH Meter',
      details: [
        'Turn on pH meter and allow 15 minutes to stabilize',
        'Rinse electrode with distilled water, blot gently',
        'Immerse in pH 7.00 buffer — adjust to read 7.00 (zero point)',
        'Rinse, then immerse in pH 4.00 buffer — adjust slope to read 4.00',
        'Rinse, then verify with pH 10.00 buffer',
        'Record actual reading for pH 10 buffer',
      ],
      safetyNote: 'Handle electrode carefully — fragile glass bulb',
      manualRef: 'Part I',
    },
    {
      number: 2,
      title: 'Standardize NaOH and Prepare Sample',
      details: [
        'Standardize NaOH by titrating 25.00 mL of 0.10 M HCl',
        'Weigh 4 aspirin tablets accurately on analytical balance',
        'Record total mass; determine average weight per tablet',
        'Crush tablets and dissolve in ~40 mL 95% ethanol in 100 mL volumetric flask',
        'Dilute to the mark with deionised water and mix thoroughly',
        'Pipet 50 mL of aspirin solution into 250 mL beaker, add 100 mL water',
        'Insert pH electrode; ensure it is clear of the stirring bar',
      ],
      manualRef: 'Part II',
    },
    {
      number: 3,
      title: 'Fill and Prepare Burette',
      details: [
        'Rinse 50 mL burette with small portions of 0.100 M NaOH',
        'Fill burette with 0.100 M NaOH',
        'Remove air bubbles from the tip',
        'Record initial burette reading',
      ],
      manualRef: 'Part III',
    },
    {
      number: 4,
      title: 'Perform Potentiometric Titration',
      details: [
        'Record initial pH of aspirin solution',
        'Add NaOH in 5.0 mL increments for the first 20.0 mL',
        'Switch to 1.0 mL increments until within 2 mL of endpoint',
        'Switch to 0.5 mL increments near the endpoint',
        'Add 0.1 mL increments as the endpoint is approached',
        'Continue titration until pH reaches ~12',
        'Record volume and pH for every addition',
      ],
      safetyNote: 'NaOH is caustic — avoid drips and spills',
      manualRef: 'Part III',
    },
    {
      number: 5,
      title: 'Data Analysis — First Derivative Method',
      details: [
        'Plot pH vs. volume of NaOH (titration curve)',
        'Calculate first derivative: ΔpH/ΔV = (pH₂ − pH₁) / (V₂ − V₁)',
        'Plot ΔpH/ΔV at midpoint volumes: V_avg = (V₁ + V₂) / 2',
        'Identify the maximum ΔpH/ΔV — this is the endpoint volume',
      ],
      manualRef: 'Part IV',
    },
    {
      number: 6,
      title: 'Calculate Aspirin Purity',
      details: [
        'moles NaOH = endpoint volume (L) × 0.100 M',
        'moles aspirin = moles NaOH (1:1 stoichiometry)',
        'mass aspirin = moles × 180.16 g/mol',
        '% purity = (mass aspirin / tablet mass) × 100',
        '% of label = (actual mg / labeled mg) × 100',
        'USP compliance requires 90–110% of labeled amount',
      ],
      manualRef: 'Part IV: Calculations',
    },
  ],
  keyFormulas: [
    'C₉H₈O₄ + NaOH → C₉H₇O₄Na + H₂O  (1:1 stoichiometry)',
    'moles NaOH = V(L) × M',
    'mass aspirin = moles × 180.16 g/mol',
    'ΔpH/ΔV = (pH₂ − pH₁) / (V₂ − V₁)  — endpoint at maximum',
    '% purity = (mass aspirin / tablet mass) × 100',
    '% of label = (actual mg / labeled mg) × 100  — USP requires 90–110%',
  ],
  tips: [
    'Allow the pH meter to warm up for at least 15 minutes before calibrating',
    'Always rinse the electrode with distilled water and blot gently between solutions',
    'Near the endpoint, the pH changes rapidly — use 0.5 mL increments, then 0.1 mL for precision',
    'Wait for the pH reading to stabilize (~10–20 seconds) before recording each value',
    'Dissolve crushed tablets in 95% ethanol; dilute to 100 mL with deionised water',
    'The first derivative plot makes endpoint identification more precise than visual inspection of the curve',
  ],
};

// ============================================================================
// Flowchart
// ============================================================================

function Flowchart() {
  // Layout: center at x=210 for 600px container, nodes 180px wide
  const cx = 210; // center x for main nodes
  const nw = 180; // node width
  const nh = 56;  // node height

  return (
    <FlowContainer width={600} height={820} title="Experiment 11: Procedure Flow">
      {/* Start terminal */}
      <FlowTerminal x={230} y={44} width={140} height={36} label="Start" color="#6366f1" />

      {/* Arrow: Start -> Calibrate */}
      <FlowArrow from={{ x: 300, y: 80 }} to={{ x: 300, y: 100 }} />

      {/* Step 1: Calibrate pH Meter */}
      <FlowNode x={cx} y={100} width={nw} height={nh} title="Calibrate pH Meter" subtitle="pH 7.00, 4.00, 10.00 buffers" color="#6366f1" />

      {/* Note: stabilization */}
      <FlowNote x={420} y={106} text="Wait 15 min to stabilize" width={145} color="#f59e0b" />

      {/* Arrow: Calibrate -> Sample prep */}
      <FlowArrow from={{ x: 300, y: 156 }} to={{ x: 300, y: 188 }} />

      {/* Step 2: Sample Preparation */}
      <FlowNode x={cx} y={188} width={nw} height={nh} title="Prepare Aspirin Sample" subtitle="4 tablets in ethanol, 100 mL flask" color="#0d9488" />

      {/* Arrow: Sample -> Burette */}
      <FlowArrow from={{ x: 300, y: 244 }} to={{ x: 300, y: 276 }} />

      {/* Step 3: Fill burette */}
      <FlowNode x={cx} y={276} width={nw} height={nh} title="Prepare Burette" subtitle="Fill with 0.100 M NaOH" color="#0d9488" />

      {/* Arrow: Burette -> Record initial pH */}
      <FlowArrow from={{ x: 300, y: 332 }} to={{ x: 300, y: 364 }} />

      {/* Step 4: Initial pH */}
      <FlowNode x={cx} y={364} width={nw} height={nh} title="Record Initial pH" subtitle="Aspirin solution (~pH 3.0)" color="#0d9488" />

      {/* Arrow: Initial pH -> Decision */}
      <FlowArrow from={{ x: 300, y: 420 }} to={{ x: 300, y: 454 }} />

      {/* Decision: Near endpoint? */}
      <FlowDecision x={265} y={454} size={70} label="Near EP?" color="#f59e0b" />

      {/* Arrow: Decision No (left) -> 1.0 mL increments */}
      <FlowArrow from={{ x: 265, y: 489 }} to={{ x: 120, y: 489 }} label="no" />
      <FlowNote x={50} y={475} text="Add 5.0 mL NaOH" width={130} color="#94a3b8" />

      {/* Arrow: Decision Yes (down) -> 0.5 mL increments */}
      <FlowArrow from={{ x: 300, y: 524 }} to={{ x: 300, y: 554 }} label="yes" />

      {/* Step: Fine increments */}
      <FlowNode x={cx} y={554} width={nw} height={nh} title="Fine Titration" subtitle="0.5 mL increments, record pH" color="#ef4444" />

      {/* Note: rapid pH change */}
      <FlowNote x={420} y={560} text="Rapid pH change zone" width={140} color="#ef4444" />

      {/* Arrow: Fine -> Check pH ~12 */}
      <FlowArrow from={{ x: 300, y: 610 }} to={{ x: 300, y: 640 }} />

      {/* Decision: pH ~12? */}
      <FlowDecision x={265} y={640} size={70} label="pH ~12?" color="#f59e0b" />

      {/* Arrow: No -> loop back (1.0 mL increments past EP) */}
      <FlowArrow from={{ x: 265, y: 675 }} to={{ x: 120, y: 675 }} label="no" />
      <FlowNote x={50} y={661} text="Add 1.0 mL, record pH" width={130} color="#94a3b8" />
      <FlowArrow from={{ x: 115, y: 661 }} to={{ x: 265, y: 640 }} dashed color="#64748b" />

      {/* Arrow: Yes -> Analyze */}
      <FlowArrow from={{ x: 300, y: 710 }} to={{ x: 300, y: 730 }} label="yes" />

      {/* Step: First Derivative Analysis */}
      <FlowNode x={cx} y={730} width={nw} height={nh} title="First Derivative Analysis" subtitle="ΔpH/ΔV → find max = endpoint" color="#6366f1" />

      {/* Arrow: Analyze -> End */}
      <FlowArrow from={{ x: 300, y: 786 }} to={{ x: 300, y: 800 }} />

      {/* Calculate purity - end terminal */}
      <FlowTerminal x={210} y={800} width={180} height={36} label="Calculate Purity" color="#6366f1" />
    </FlowContainer>
  );
}

// ============================================================================
// Export
// ============================================================================

export default function Exp11Summary() {
  return <ExperimentSummary data={summaryData} flowchart={<Flowchart />} />;
}
